<template>
    <div class="abc-score-layout">
        <div :id="renderId" class="abc-notation"></div>
        <div class="abc-play-button">
            <IonButton fill="clear" @click="togglePlay($event)" size="small">{{ isPlaying ? 'Stop' : 'Play' }}</IonButton>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import abcjs from "abcjs";
import { IonButton } from "@ionic/vue";

defineOptions({
    name: "abc-score",
});

const props = defineProps({
    abcCode: { default: "" },
    abcCodeId: { default: 0 },
});

const midiPlayer = ref(null);
const isPlaying = ref(false);
const soundFontUrl =
    "https://paulrosen.github.io/midi-js-soundfonts/FluidR3_GM/";
const renderId =
    "abcjs-" +
    props.abcCodeId +
    "-" +
    Math.random().toString(36).substring(2, 9);

/** Must match every renderAbc call on this element, or the score jumps in size when the synth re-renders (first Play). */
const abcRenderOptions = { responsive: "resize" };

function renderAbcCode() {
    nextTick(() => {
        if (props.abcCode) {
            abcjs.renderAbc(renderId, props.abcCode, abcRenderOptions);
        }
    });
}

function startPlayback() {
    abcjs.synth.registerAudioContext();
    const ac = abcjs.synth.activeAudioContext();

    const primeAndStart = (player) => {
        if (!player) {
            return;
        }
        player
            .prime()
            .then(() => {
                player.start();
                isPlaying.value = true;
            })
            .catch((error) => {
                console.error("MIDI Player prime/start failed:", error);
                isPlaying.value = false;
            });
    };

    const begin = () => {
        if (!props.abcCode) {
            return;
        }
        if (!midiPlayer.value) {
            const player = new abcjs.synth.CreateSynth();
            midiPlayer.value = player;
            player
                .init({
                    visualObj: abcjs.renderAbc(
                        renderId,
                        props.abcCode,
                        abcRenderOptions
                    )[0],
                    options: {
                        soundFontUrl,
                        qpm: 120, // slower than abcjs default 180
                        onEnded: () => {
                            isPlaying.value = false;
                        },
                    },
                })
                .then(() => {
                    if (midiPlayer.value !== player) {
                        return;
                    }
                    primeAndStart(player);
                })
                .catch((error) => {
                    console.error("MIDI Player initialization failed:", error);
                    if (midiPlayer.value === player) {
                        midiPlayer.value = null;
                    }
                    isPlaying.value = false;
                });
        } else {
            primeAndStart(midiPlayer.value);
        }
    };

    const resumePromise =
        ac.state === "suspended" || ac.state === "interrupted"
            ? ac.resume().catch(() => {})
            : Promise.resolve();

    resumePromise.then(begin);
}

function togglePlay(event) {
    event.stopPropagation();
    if (isPlaying.value) {
        if (midiPlayer.value) {
            midiPlayer.value.stop();
        }
        isPlaying.value = false;
        return;
    }
    if (!props.abcCode) {
        return;
    }
    startPlayback();
}

watch(
    () => props.abcCode,
    (newCode) => {
        if (midiPlayer.value) {
            midiPlayer.value.stop();
            midiPlayer.value = null;
        }
        isPlaying.value = false;
        if (newCode) {
            renderAbcCode();
        }
    }
);

onMounted(() => {
    nextTick(() => {
        if (props.abcCode) {
            renderAbcCode();
        }
        window.addEventListener("resize", renderAbcCode);
    });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", renderAbcCode);
    if (midiPlayer.value) {
        midiPlayer.value.stop();
        midiPlayer.value = null;
    }
    isPlaying.value = false;
});
</script>

<style scoped>
.abc-score-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 100px;
    gap: 8px;
    width: 100%;
}

.abc-notation {
    min-width: 0;
}

.abc-play-button {
    display: flex;
    justify-content: center;
    align-items: center;
}

@media (max-width: 576px) {
    .abc-score-layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .abc-play-button {
        justify-content: flex-start;
    }
}
</style>
